import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  BookOpen,
  CalendarDays,
  ChevronDown,
  CircleDollarSign,
  ClipboardList,
  Compass,
  HelpCircle,
  Layers3,
  ListChecks,
  LogOut,
  Network,
  Search,
  ShieldCheck,
  Smartphone,
  Trophy,
  UsersRound,
  WalletCards,
  Workflow,
} from "lucide-react";
import "./LandingPage.css";
import "./SoftwareGuide.css";

const GUIDE_SECTIONS = [
  {
    id: "getting-started",
    title: "Getting Started",
    eyebrow: "First setup path",
    summary:
      "Use this section when a cardroom is opening the software for the first time or training a new operator.",
    icon: Compass,
    topics: [
      {
        title: "Recommended first setup order",
        body: [
          "The fastest way to prepare a room is to follow the same order used by the first-time tutorial modal. That flow keeps setup work separated from live table operations.",
          "The tutorial modal can still be opened from Admin. The Software Guide is the deeper reference users can return to when they need more context.",
        ],
        steps: [
          "Learn the navigation so staff know where setup, live operations, reports, and tournaments live.",
          "Add players on the Players page. Players can be created one at a time or imported with CSV.",
          "Add dealers and service staff on the Staff page so tips and payouts can be tracked correctly.",
          "Add wallets on the Session page before recording electronic deposits, withdrawals, tips, costs, or payouts.",
          "Start the first session on the Session page and verify the cash bag.",
          "Return to the Game dashboard, create tables, seat players, and start each table from the table header.",
        ],
      },
      {
        title: "The three major operating zones",
        body: [
          "Nexus is easiest to understand as three connected work areas: Game Operations, Back Office, and Tournaments.",
        ],
        bullets: [
          "Game Operations includes Players, Staff, Session, Wallets, and the Game dashboard. This is where live cash-game work happens.",
          "Back Office includes Dashboard, Admin, Referrals, Book, Schedule, and Data. This is where owners and managers control configuration, permissions, reporting, and historical records.",
          "Tournaments includes tournament setup, templates, running tournaments, tables, points, reports, and display screens.",
        ],
      },
      {
        title: "What must exist before live activity",
        body: [
          "Players, dealers, staff, books, and wallets can be created before live activity begins. Not every item is required, but missing setup creates friction later.",
        ],
        bullets: [
          "Players can be created before books exist. A player can be assigned to a book later.",
          "Wallets should exist before any transaction is recorded to that payment method.",
          "A cash session should be active before tables are started and game activity is recorded.",
          "Credit limits should be reviewed before staff begin issuing credit.",
        ],
      },
      {
        title: "A practical first-day checklist",
        body: [
          "For a new room, start with the minimum setup needed to record the day accurately, then add advanced configuration as the team becomes comfortable.",
        ],
        bullets: [
          "Create or import the player list.",
          "Create dealers and any service staff who can receive tips.",
          "Add electronic wallets and optionally add a house wallet if the room needs one.",
          "Review Admin settings for waitlist duration, mobile access, default player settings, bad beat, referral and referee bonuses, and billing.",
          "Start the session, verify the cash bag, create tables, and record all deposits, withdrawals, credit, tips, rake, and costs inside the system.",
        ],
      },
    ],
  },
  {
    id: "core-concepts",
    title: "Core Concepts",
    eyebrow: "How the system fits together",
    summary:
      "These ideas appear throughout the app and are worth understanding before using the detailed pages.",
    icon: Layers3,
    topics: [
      {
        title: "Sessions group operational activity",
        body: [
          "A session usually represents an operational day or event period, but it is more flexible than a calendar day because a poker game can continue past midnight.",
          "Sessions group together tables, player transactions, rake, tips, costs, cash bag changes, wallet movements, and reports so the room can later understand exactly what happened during that operating period.",
        ],
      },
      {
        title: "Tables happen inside sessions",
        body: [
          "Starting a session and starting a table are different actions. The session creates the operational container. Tables are created and run inside that container.",
          "Users should start the session from the Session page before running live tables on the Game dashboard. The system also prevents ending a session while players are still seated.",
        ],
      },
      {
        title: "Players are shared across cash and tournaments",
        body: [
          "The player database is shared. The same player can appear in cash games, tournament entries, referrals, books, exports, reports, and mobile account linking.",
          "This shared player record is one reason the book/account balance can connect cash-game and tournament activity.",
        ],
      },
      {
        title: "Cash operations and tournament operations are separate where they need to be",
        body: [
          "Cash-game sessions, cash wallets, and the cash-game cash bag are separate from tournament wallets and the tournament cash bag.",
          "This separation matters because many rooms treat tournament money and cash-game money as different operating pools, even when the same people and payment methods are involved.",
        ],
      },
      {
        title: "The guide is a reference, not a permission bypass",
        body: [
          "Any logged-in user can read the guide. Reading about a feature does not give that user permission to use the feature.",
          "If a user does not have access to a page or action, the app's normal permission rules still apply.",
        ],
      },
    ],
  },
  {
    id: "game-operations",
    title: "Game Operations",
    eyebrow: "Live cash-game workflow",
    summary:
      "Use Game Operations to prepare the room, run sessions, manage tables, and record live activity.",
    icon: Workflow,
    topics: [
      {
        title: "Session page: the session hub",
        body: [
          "The Session page is where a room starts and ends cash sessions, verifies the cash bag, manages costs, adds service staff tips, and creates wallets.",
          "Before opening tables, start the session and verify the physical cash the house is holding. This gives the room a clean operational starting point.",
        ],
        bullets: [
          "Start Session begins the operating period.",
          "End Session closes the operating period when tables are clear and blocking items are resolved.",
          "Cash Bag tracks physical cash held by the house.",
          "Costs record session expenses and can be paid now or left unpaid.",
          "Service Staff Tips record chip tips for non-dealer workers.",
          "Wallets track electronic payment methods and settlement balances.",
        ],
      },
      {
        title: "Ending a session",
        body: [
          "A session should not be ended while live table activity is still unresolved. The app blocks ending a session while players are seated, and unpaid costs can also prevent a clean closeout.",
          "When a session ends, unresolved player session credit is reconciled into the shared book/account balance. If a player also has a positive balance, the system automatically combines the amounts and offsets the credit against that book balance.",
          "Some actions can still happen outside an active session, such as costs, player debt settlements, and staff payouts. Those actions may be associated with the last active session depending on the workflow.",
        ],
      },
      {
        title: "Game dashboard: creating and operating tables",
        body: [
          "The Game dashboard is the live control surface. This is where tables are created, players are seated, waitlists are managed, mobile requests are handled, and table-level activity is recorded.",
        ],
        bullets: [
          "Create a table with a table name, game type, stakes, bomb pot settings, and optional advanced settings.",
          "Advanced table settings can include max players, scheduled start time, public/private visibility, request-only mode, and registration timing.",
          "After a session is active and the table is ready, start the table from the green icon in the table header.",
        ],
      },
      {
        title: "RSVP, waitlist, and seated players",
        body: [
          "A table can have players in different states depending on whether the game is preparing, waiting, or running.",
        ],
        bullets: [
          "RSVP is mainly for interest and reserved spots before a table has started or before seats are finalized.",
          "Waitlist is for players waiting to be seated. Order matters, and users can move players up or down.",
          "Waitlist expiration helps staff track when the first waiting player has had enough time to respond before the next player is contacted.",
          "Seated players are the players physically at the table.",
        ],
      },
      {
        title: "Public, private, and request-only tables",
        body: [
          "Public/private controls whether a table appears on Nexus Mobile. Request-only controls whether mobile requests require staff approval.",
        ],
        bullets: [
          "Public tables can appear to mobile users so they can request RSVP or waitlist spots.",
          "Private tables stay internal to staff.",
          "Request-only tables require the room to approve mobile requests before players are added.",
          "If request-only is off, eligible mobile requests can be placed automatically on the list.",
        ],
      },
      {
        title: "Quick player tools on the Game dashboard",
        body: [
          "The Game dashboard includes tools that reduce page switching during live work.",
        ],
        bullets: [
          "Phone lookup and code lookup help staff check whether a player already exists before creating a duplicate.",
          "Quick-add lets staff add a new player from the Game page while staying in the live workflow.",
          "Pending mobile requests can be linked to an existing player or used to create a new player record.",
        ],
      },
      {
        title: "Live table actions",
        body: [
          "Once a table is running, staff can record deposits, withdrawals, credit buy-ins, book transactions, rake, dealer tips, bad beat activity, player history, cashouts, and player movement.",
          "Each transaction can affect several areas at once, including player credit, book balance, wallet balance, cash bag, session totals, and reports.",
        ],
      },
    ],
  },
  {
    id: "money-credit-books-wallets",
    title: "Money, Credit, Books, and Wallets",
    eyebrow: "The most important model",
    summary:
      "This section explains how money movement, credit, book balances, wallets, and cash bag tracking relate to each other.",
    icon: WalletCards,
    topics: [
      {
        title: "Cash bag",
        body: [
          "The cash bag is the amount of physical cash the house is holding at a given point in time. It is not tied to a specific staff member.",
          "If one staff member receives cash and another staff member later pays cash out, the system treats that as house cash. The goal is to track the room's total physical cash position.",
        ],
        bullets: [
          "Cash deposits should increase the cash bag.",
          "Cash withdrawals and cash payouts should decrease the cash bag.",
          "Cash bag verification helps staff compare what the software says against the physical cash actually held.",
        ],
      },
      {
        title: "Wallets",
        body: [
          "Wallets represent electronic payment methods or tracked payment accounts. A wallet must exist before a transaction can be recorded to it.",
          "The app already displays whether a wallet is owed or owes. Users should rely on that displayed label instead of trying to interpret the raw sign of the balance.",
        ],
        bullets: [
          "Use wallets for electronic deposits, withdrawals, costs, tips, payouts, and settlements.",
          "A house wallet can be added if the room needs a tracked internal wallet.",
          "Wallet balances can be settled or cleared when appropriate.",
        ],
      },
      {
        title: "Session credit",
        body: [
          "Cash-game session credit is credit issued during the current cash session. It is separate from tournament credit.",
          "If a player receives chips on credit and does not settle that credit by the end of the session, the remaining credit can transfer into the player's shared book/account balance.",
          "If the session ends while the player has both session credit and a positive book/account balance, the system automatically combines them. The positive balance is applied against the credit, so only the remaining net amount continues forward in the book.",
        ],
      },
      {
        title: "Book and account balance",
        body: [
          "A book tracks who is responsible for a player or group of players' credit, debt, and account balance. It is useful for partnerships, agent/player setups, or rooms where the house is not directly responsible for every player's result.",
          "At the player level, the book/account balance is the amount the player owes or is owed. This balance is shared across cash games and tournaments.",
        ],
        bullets: [
          "If a player buys in on credit and does not pay, the unpaid amount can become book debt.",
          "If a player wins and does not take payment immediately, the amount can remain as a balance owed to the player.",
          "If a group of players belongs under one person's book, that book owner can be responsible for the group result.",
        ],
      },
      {
        title: "Cash, wallet, credit, and book: when to use each",
        body: [
          "Choosing the correct transaction type keeps reports, cash bag, wallets, and player balances accurate.",
        ],
        bullets: [
          "Use cash when physical cash is exchanged and the cash bag should change.",
          "Use wallet when money moves through an electronic payment method or tracked account.",
          "Use credit when the player receives chips without paying immediately.",
          "Use book when debt or balance needs to carry beyond the session or be adjusted at the account level.",
        ],
      },
      {
        title: "Credit limits",
        body: [
          "Credit limits should consider both current session credit and existing book debt. A player with existing debt should have less remaining available credit.",
        ],
        example: {
          title: "Credit limit example",
          body: [
            "If a player has a 500 credit limit and already has 300 in book debt, the maximum additional credit available should be 200.",
          ],
        },
      },
      {
        title: "Settle and roll balance",
        body: [
          "The Settle and Roll Balance options help staff handle credit and remaining balances during withdrawals or cashouts.",
        ],
        bullets: [
          "Settle automatically subtracts what the player owes from the withdrawal or cashout when the player is on credit.",
          "Roll Balance lets a player cash out less than they are owed, or carry a remaining balance or debt forward instead of fully settling immediately.",
          "For mixed withdrawals, if a player has 500 in credit and settle is selected, the first entry can automatically account for that 500 before the remaining payout is split.",
        ],
      },
      {
        title: "Clearing balances and debts",
        body: [
          "Clearing balance and clearing debt are important account actions. They should be used when money is actually paid out, collected, or otherwise intentionally resolved.",
        ],
        bullets: [
          "Clear player balance when the room pays a player money that was owed to that player.",
          "Clear player debt when the room collects money from a player who owed money.",
          "Player-to-player book transactions can be used when both parties agree that one player or agent is taking responsibility for another player's balance.",
        ],
      },
      {
        title: "Deleting or undoing transactions",
        body: [
          "Deleting or undoing a transaction reverses everything that transaction did. This can affect player balances, credit, wallet balances, cash bag, session totals, reports, and history.",
        ],
        warning:
          "Treat transaction deletion as a correction tool, not a normal workflow. Review what the transaction touched before removing it.",
      },
      {
        title: "Chips on tables and operational exposure",
        body: [
          "Chips on tables helps staff understand how many chips are currently out with players. Combined with total session credit, it gives the room a better view of operational exposure.",
        ],
      },
      {
        title: "Debt gained and balance gained",
        body: [
          "Debt gained and balance gained are timeframe-based report numbers. They can be positive or negative depending on the selected dates and activity.",
        ],
        bullets: [
          "Debt gained means the amount players owe increased. If a player owed 100 and now owes 200, debt gained is 100.",
          "Balance gained means the amount owed to players increased. If a player was owed 100 and is now owed 200, balance gained is 100.",
        ],
      },
    ],
  },
  {
    id: "players-dealers-staff",
    title: "Players, Dealers, and Staff",
    eyebrow: "People records",
    summary:
      "Use these pages to manage player profiles, dealer records, service staff, mobile links, and payouts.",
    icon: UsersRound,
    topics: [
      {
        title: "Players page",
        body: [
          "The Players page is the main player directory. It stores player names, contact details, notes, mobile links, book assignments, session credit, account balance, and activity context.",
        ],
        bullets: [
          "Add players one at a time when staff are building the list manually.",
          "Use CSV upload when the room already has an existing player list.",
          "Use CSV export for backups, review, recordkeeping, or external analysis.",
          "Assign players to books when a person or agent is responsible for their balance.",
          "Set Referred By when a new player was brought in by another player.",
        ],
      },
      {
        title: "Setting a player's referral",
        body: [
          "When adding a new player, staff can select which existing player referred them. This connects the new player to the person who brought them in.",
          "The referral can still be changed before the new player makes their first deposit. After the first deposit, the referral is locked so bonuses and referral history stay consistent.",
        ],
        bullets: [
          "Use Referred By during player creation when the referral is already known.",
          "If staff choose the wrong referrer or learn the referral later, edit it before the player's first deposit.",
          "After the first deposit, treat the referral as final.",
        ],
      },
      {
        title: "Avoiding duplicate players",
        body: [
          "Phone lookup, player codes, mobile linking, and careful name review help prevent duplicate player records.",
          "Duplicate player records can split history, balances, referrals, first-deposit bonus logic, and mobile activity across multiple profiles, so it is worth checking before adding a new player.",
        ],
      },
      {
        title: "Mobile account linking",
        body: [
          "Players can connect through Nexus Mobile. Once connected, they can join the club, see recent transaction history for the last session, view active tables and seats, request chips, and request RSVP or waitlist spots.",
          "When a mobile request comes in, staff can link the request to an existing player or create a new player if needed.",
        ],
      },
      {
        title: "Dealers",
        body: [
          "Dealers are the people dealing cards at tables, collecting rake, and receiving dealer tips.",
          "Dealer records support tip tracking, payout tracking, mobile invites, history, and session stats.",
        ],
      },
      {
        title: "Service staff",
        body: [
          "Service staff are non-dealer workers who may receive chips from tables. Examples include servers, waitresses, cooks, massage staff, or other workers.",
          "Service staff tips should be used when chips are exchanged for money. Salary or non-chip compensation should be entered as a cost instead.",
        ],
      },
      {
        title: "Dealer and staff payouts",
        body: [
          "Dealer and staff payouts support cash, wallet, and split payment workflows. They also track owed amounts when the room does not pay immediately.",
        ],
        bullets: [
          "Use payout history to review past dealer or staff payments.",
          "Use owed amounts when chips were earned but cash or wallet settlement happens later.",
          "Use costs for payroll or salary-style expenses that are not chip-for-money payouts.",
        ],
      },
      {
        title: "Dealer mobile access",
        body: [
          "Dealers can have their own login. Dealer mobile access can help them interact with schedules, request shifts, and view their session stats.",
        ],
      },
    ],
  },
  {
    id: "back-office",
    title: "Back Office",
    eyebrow: "Management pages",
    summary:
      "Back Office pages help owners and managers control settings, books, reporting, schedules, referrals, and detailed records.",
    icon: ClipboardList,
    topics: [
      {
        title: "Dashboard",
        body: [
          "Dashboard is the high-level business overview. It is best for quickly understanding performance across a selected timeframe.",
        ],
        bullets: [
          "Review profit, rake, costs, deposits, withdrawals, tips, debt changes, balance changes, and trends.",
          "Use charts and session summaries to see how the business is performing.",
          "Use Dashboard when the question is broad, such as how the week, month, or year is going.",
        ],
      },
      {
        title: "Data",
        body: [
          "Data is the detailed ledger and lookup area. It is best when the user already knows what they want to investigate.",
        ],
        bullets: [
          "Search player, wallet, dealer, and staff transaction history.",
          "Review all sessions and drill into tables, tournaments, payouts, cash, book, wallets, and bonuses.",
          "Export detailed CSV records for backups, review, and external recordkeeping.",
        ],
      },
      {
        title: "Book",
        body: [
          "Book Manager is where account balances, book ownership, player-to-player book transactions, credit limits, and book exports are handled.",
          "Because the shared book balance can be affected by both cash and tournament activity, the Book page is one of the most important financial reference pages.",
        ],
      },
      {
        title: "Admin",
        body: [
          "Admin controls users, invites, permissions, player join requests, cardroom settings, billing, mobile settings, support, reviews, and the tutorial modal.",
          "This is also where the Software Guide is currently linked.",
        ],
      },
      {
        title: "Referrals and Schedule",
        body: [
          "Referrals manages agent stats, referral stats, referred players, reward settings, caps, and payout history. Schedule manages events, dealer invites, and dealer shift requests.",
          "Neither page is required to run a basic cash game, but both help replace scattered tools like spreadsheets, text threads, and paper notes.",
        ],
      },
    ],
  },
  {
    id: "admin-permissions",
    title: "Admin and Permissions",
    eyebrow: "Control access",
    summary:
      "Admin settings allow the room to control who can see information, change settings, invite users, and perform sensitive actions.",
    icon: ShieldCheck,
    topics: [
      {
        title: "User access",
        body: [
          "Admin permissions are highly customizable. The guide does not need to teach internal permission letters, but owners should understand the practical effect of access control.",
        ],
        bullets: [
          "Some users may only need live game tools.",
          "Some users may need Back Office access but not sensitive financial changes.",
          "Some users may need tournament access only.",
          "Dealer-only users can be limited to dealer-specific areas.",
        ],
      },
      {
        title: "Protecting sensitive actions",
        body: [
          "Permissions can restrict users from seeing numbers and stats, creating or changing tables, modifying player books, changing credit limits, deleting players, inviting users, or accessing sensitive areas.",
          "For new staff, start with the minimum access needed for their job and expand later if necessary.",
        ],
      },
      {
        title: "Cardroom settings",
        body: [
          "Cardroom Settings control room defaults and behavior across cash games, tournaments, mobile access, and general account configuration.",
        ],
        bullets: [
          "Cash settings can include waitlist duration, default credit limit, bad beat behavior, referral bonuses, referee bonuses, and first-time bonuses.",
          "Tournament settings can include defaults such as tournament tables.",
          "Nexus Mobile settings include discoverability, public join behavior, access code, and default accept/reject messages.",
          "General settings include billing, membership, images, reviews, and other account-level controls.",
        ],
      },
      {
        title: "Billing and membership",
        body: [
          "Billing and membership settings should be documented because they control subscription status, renewal timing, payment methods, invoices, and Stripe portal access.",
        ],
      },
      {
        title: "Player join requests",
        body: [
          "Admin can process player join requests from Nexus Mobile. Accept and reject messages can be configured so staff do not have to rewrite the same response each time.",
        ],
      },
    ],
  },
  {
    id: "schedule",
    title: "Schedule",
    eyebrow: "Events and staffing",
    summary:
      "Schedule helps the room plan events and staff coverage without relying on separate spreadsheets or message threads.",
    icon: CalendarDays,
    topics: [
      {
        title: "What Schedule is for",
        body: [
          "Schedule is not required to run a basic game, but it is recommended for productivity. It can replace Google Sheets, text threads, and manual planning.",
          "The calendar gives staff and managers a shared place to view events and staffing plans.",
        ],
      },
      {
        title: "Creating events",
        body: [
          "Events can include cash-game or tournament-related scheduling. The event can include a title, description, time, and type.",
        ],
      },
      {
        title: "Dealer invites and requests",
        body: [
          "Managers can invite dealers to scheduled events, and dealers can request shifts depending on access and workflow.",
          "Approving, declining, and deleting requests keeps staffing records organized and reduces the need for separate communication tools.",
        ],
      },
      {
        title: "Best use",
        body: [
          "Use Schedule when the room needs a reliable staffing plan, a calendar view of upcoming games or tournaments, and a record of who was invited or requested for each event.",
        ],
      },
    ],
  },
  {
    id: "referrals",
    title: "Referrals",
    eyebrow: "Agents, referrals, and rewards",
    summary:
      "Referrals track people who bring players to the game, player referral links, first-deposit bonuses, agent stats, referral stats, caps, and payout history.",
    icon: Network,
    topics: [
      {
        title: "Agents",
        body: [
          "Agents are players or people who get rewarded for bringing players to the game. Agents can be tied to referred players, reward rates, caps, and payout history.",
        ],
      },
      {
        title: "Player referrals",
        body: [
          "A player can be marked as referred by another player when the new player is added. This is useful when the room wants to track who brought a new player in, even outside a more formal agent agreement.",
          "The referred-by player can be changed until the new player makes their first deposit. Once that first deposit happens, the referral is locked.",
        ],
        bullets: [
          "Set the referrer during player creation whenever possible.",
          "Correct the referrer before the first deposit if staff entered the wrong player or learned the referral later.",
          "After first deposit, the referral should stay fixed because bonus distribution and referral stats depend on it.",
        ],
      },
      {
        title: "Referral bonuses",
        body: [
          "If a player has a referrer when they make their first deposit, referral bonuses are distributed based on the Referral Bonus and Referee Bonus values set in Admin.",
          "The referral bonus rewards the player who referred the new player. The referee bonus rewards the new player who was referred.",
        ],
        bullets: [
          "Referral bonuses trigger from the referred player's first deposit.",
          "If no referrer is set before the first deposit, the player should be treated as not having a referral for that first-deposit bonus flow.",
          "First-time bonuses are separate from referral and referee bonuses and should be reviewed in Admin settings.",
        ],
      },
      {
        title: "Caps and status",
        body: [
          "Referral rewards can have maximum lifetime limits and active, paused, or capped states. These controls help the room avoid overpaying or continuing rewards after the agreement has ended.",
        ],
      },
      {
        title: "Referral stats",
        body: [
          "The Referrals page shows referral stats alongside agent stats. This helps users separate formal agent performance from general player referral activity.",
          "Use these stats to understand who is bringing players in, which referred players made deposits, and how referral bonuses are affecting the room.",
        ],
      },
      {
        title: "Agents and books",
        body: [
          "It is common for agents to also be connected to books. In some rooms, an agent may be responsible for the balances or debt of the players they bring.",
          "When that is true, the Book page and Referrals page should be used together so financial responsibility and reward tracking stay clear.",
        ],
      },
    ],
  },
  {
    id: "reports-dashboard-data-exports",
    title: "Reports, Dashboard, Data, and Exports",
    eyebrow: "Understanding results",
    summary:
      "Use reports to understand performance, investigate details, and export records for review or backup.",
    icon: BarChart3,
    topics: [
      {
        title: "Dashboard is for overview",
        body: [
          "Dashboard is visual and timeframe-based. It helps owners understand how the business is doing without digging through individual ledger entries first.",
        ],
        bullets: [
          "Use last session, week, month, year, or custom date ranges.",
          "Review summary cards, trends, calendar-style performance, session details, dealer analytics, and player analytics.",
          "Use Dashboard when the question is about overall performance.",
        ],
      },
      {
        title: "Data is for investigation",
        body: [
          "Data is rawer and more detailed. Use it for specific lookups, transaction history, ledgers, session details, and exports.",
        ],
        bullets: [
          "Search by player, wallet, dealer, staff, transaction type, and date range.",
          "Open detailed session records and inspect tables, tournaments, payouts, cash, book, wallets, and bonuses.",
          "Use Data when the question is about a specific person, transaction, session, or ledger.",
        ],
      },
      {
        title: "Profit and operational numbers",
        body: [
          "Reports can include net profit, rake, costs, deposits, withdrawals, dealer tips, staff tips, debt changes, balance changes, and operating flow.",
          "Some numbers are operational indicators rather than simple cash-in/cash-out totals. Users should read them together instead of isolating one number without context.",
        ],
      },
      {
        title: "Debt gained and balance gained",
        body: [
          "These two numbers are often confusing, so they should be explained anywhere reports are discussed.",
        ],
        bullets: [
          "Debt gained means players owe more than they did before the timeframe. Example: owed 100 before, owed 200 after, debt gained is 100.",
          "Balance gained means the room owes players more than before the timeframe. Example: player was owed 100 before, owed 200 after, balance gained is 100.",
          "Both numbers can go negative if debt or balance decreases during the selected timeframe.",
        ],
      },
      {
        title: "Exports",
        body: [
          "CSV exports are useful for backups, external review, tax or accounting support, and keeping independent records.",
          "Exports should be mentioned in the pages where they appear and also understood as part of the broader reporting workflow.",
        ],
      },
    ],
  },
  {
    id: "tournaments",
    title: "Tournaments",
    eyebrow: "Tournament operations",
    summary:
      "Tournament tools cover templates, creation, running events, tables, payouts, points, reports, and display screens.",
    icon: Trophy,
    topics: [
      {
        title: "Tournament overview",
        body: [
          "Tournament operations share some ideas with cash-game operations, but tournament wallets, tournament cash bag, tournament transactions, and tournament credit are separate.",
          "Players still come from the shared player database, and final book/account effects can still reach the shared book.",
        ],
      },
      {
        title: "Templates versus tournaments",
        body: [
          "Structure templates define blind structures, level timing, chip counts, add-on chip amounts, rebuy settings, breaks, antes, and payout structure behavior.",
          "Templates do not define the actual dollar amounts for a specific event. Buy-ins and dollar amounts are handled when creating an actual tournament and choosing a template.",
        ],
      },
      {
        title: "Saved tournament setups",
        body: [
          "Saved tournament setups help the room reuse common event configurations. A room can save both structure templates and tournament setups so recurring events are faster to launch.",
        ],
      },
      {
        title: "Creating a tournament",
        body: [
          "Tournament creation combines the selected structure template with event-specific settings such as name, buy-in, countdown, points, buy-in/add-on/rebuy costs, rake, tips, custom fees, bounty amount, and other options.",
        ],
      },
      {
        title: "Tournament wallets and cash bag",
        body: [
          "Tournament wallets and tournament cash bag are separate from cash-game wallets and the cash-game cash bag.",
          "Even if the same person or wallet name exists in both areas, tournament and cash operations use separate wallet and cash tracking because many rooms run them as separate operating pools.",
        ],
      },
      {
        title: "Tournament Manager",
        body: [
          "Tournament Manager is the main live tournament control page. It handles the clock, levels, transactions, player lists, tables, seating, eliminations, payouts, ICM, winner tips, costs, and activity history.",
        ],
        bullets: [
          "Use Process Transaction for buy-ins, rebuys, add-ons, paybacks, cashouts, costs, bounties, and payment allocation.",
          "Use table tools for seating, unseated players, table movement, final table creation, table display, and randomization.",
          "Use payout tools for payout structures, custom payouts, ICM, and winner settlement.",
          "Use Recent Activity to review and potentially undo tournament actions.",
        ],
      },
      {
        title: "Tournament credit and the shared book",
        body: [
          "Tournament credit is separate from cash-game session credit. However, tournament credit can still eventually affect the same shared player book/account balance.",
          "This idea should be explained in both the Money section and Tournament section because users may look for it from either place.",
        ],
      },
      {
        title: "Payouts, ICM, bounties, add-ons, rebuys, and winner tips",
        body: [
          "These are core tournament concepts and should be documented as part of tournament settings and operations.",
        ],
        bullets: [
          "Payout structures define how prize money is distributed.",
          "ICM helps calculate payouts in certain tournament settlement situations.",
          "Bounties track bounty-related money separately from ordinary prizepool logic.",
          "Rebuys and add-ons can affect entries, chips, prizepool, and player cost.",
          "Winner tips should be tracked so tournament financials remain accurate.",
        ],
      },
      {
        title: "Tournament points and seasons",
        body: [
          "Tournament Points lets the room run leaderboard-style seasons. Players can earn points from entries, placements, presets, multipliers, and manual adjustments.",
          "Season reset should be treated carefully because it resets current tournament points while keeping historical records.",
        ],
      },
      {
        title: "Tournament display screens",
        body: [
          "Tournament Screen and Tournament Table View are display/player-facing screens. They are useful for showing tournament clock, payouts, tournament information, and table seating during the event.",
        ],
      },
    ],
  },
  {
    id: "nexus-mobile",
    title: "Nexus Mobile and Mobile Requests",
    eyebrow: "Connected player and dealer experience",
    summary:
      "Nexus Mobile connects players and dealers to the room while staff keep approval and operational control.",
    icon: Smartphone,
    topics: [
      {
        title: "Player mobile access",
        body: [
          "Players can use Nexus Mobile to join the club, identify the cardroom through its access code, view recent transaction history for the last session, see active tables and open seats, request chips, and request RSVP or waitlist spots.",
        ],
      },
      {
        title: "Mobile discovery and access code",
        body: [
          "Admin settings control whether the room appears in mobile discovery, whether public joins are allowed, and what access code players use to find the club.",
          "Accept and reject messages can be configured so join request communication is consistent.",
        ],
      },
      {
        title: "Pending requests",
        body: [
          "Mobile requests can appear on the Game dashboard and in Admin depending on the request type. Staff can approve, reject, link to an existing player, or create a player record from the request.",
        ],
      },
      {
        title: "Table visibility and request flow",
        body: [
          "Public tables can appear on Nexus Mobile. Private tables remain staff-only. Request-only tables require staff approval before players are added to RSVP or waitlist.",
        ],
      },
      {
        title: "Dealer mobile access",
        body: [
          "Dealers can receive mobile access to interact with schedules, request shifts, and review session stats. Dealer mobile access is separate from player mobile use.",
        ],
      },
    ],
  },
  {
    id: "common-mistakes",
    title: "Common Mistakes and Best Practices",
    eyebrow: "Operational habits",
    summary:
      "These reminders help keep ledgers clean and prevent confusing reports later.",
    icon: AlertTriangle,
    topics: [
      {
        title: "Add wallets before recording wallet transactions",
        body: [
          "A transaction cannot be recorded to a wallet that does not exist. Add wallets during setup so deposits, withdrawals, costs, tips, and payouts can be recorded correctly when the room is busy.",
        ],
      },
      {
        title: "Start the session before running tables",
        body: [
          "A session groups live activity. Starting tables without understanding the active session state causes confusion when reviewing reports later.",
        ],
      },
      {
        title: "Verify the cash bag",
        body: [
          "The software tracks house cash, but staff still need to compare it against physical cash. Regular verification catches mistakes early.",
        ],
      },
      {
        title: "Use the book for carried balances",
        body: [
          "If a player cannot or does not settle by the end of a session, book/account balance is the proper place for that debt or balance to continue forward.",
        ],
      },
      {
        title: "Do not casually delete transactions",
        body: [
          "Undoing or deleting a transaction can reverse player balances, credit, cash bag, wallet balances, reports, and session totals. Use it only when correcting a mistake.",
        ],
      },
      {
        title: "Keep tournament and cash money separated",
        body: [
          "Tournament wallets and tournament cash bag are separate from cash-game wallets and cash-game cash bag. Treat them as separate operating pools unless the room intentionally settles between them.",
        ],
      },
      {
        title: "Use permissions intentionally",
        body: [
          "Give users enough access to do their job, but avoid giving sensitive financial or settings permissions by default.",
        ],
      },
      {
        title: "Export important records",
        body: [
          "CSV exports are helpful for backups, external review, accounting support, and long-term recordkeeping.",
        ],
      },
    ],
  },
  {
    id: "glossary",
    title: "Glossary",
    eyebrow: "Quick definitions",
    summary:
      "Use this section when a user needs a short definition without reading a full workflow.",
    icon: HelpCircle,
    terms: [
      {
        term: "Account balance",
        definition:
          "The player-level amount owed by the player or owed to the player. It is part of the shared book/account model.",
      },
      {
        term: "Agent",
        definition:
          "A person or player who brings players to the room and may receive referral rewards.",
      },
      {
        term: "Balance gained",
        definition:
          "The amount owed to players increased during a selected timeframe. It can also decrease and show as negative.",
      },
      {
        term: "Book",
        definition:
          "A way to track responsibility for a player or group of players' credit, debt, and account balance.",
      },
      {
        term: "Cash bag",
        definition:
          "The amount of physical cash the house is holding. It is house cash, not staff-member-specific cash.",
      },
      {
        term: "Credit",
        definition:
          "Chips or tournament value given before payment is collected. Cash session credit and tournament credit are separate.",
      },
      {
        term: "Credit limit",
        definition:
          "The maximum credit a player should be able to receive, considering both current credit and existing book debt.",
      },
      {
        term: "Data",
        definition:
          "The detailed history, ledger, lookup, and export area.",
      },
      {
        term: "Dashboard",
        definition:
          "The visual high-level reporting area for summaries, charts, trends, and business performance.",
      },
      {
        term: "Debt gained",
        definition:
          "The amount players owe increased during a selected timeframe. It can also decrease and show as negative.",
      },
      {
        term: "Dealer",
        definition:
          "A worker who deals cards, collects rake, and can receive dealer tips.",
      },
      {
        term: "ICM",
        definition:
          "A tournament payout calculation method used in certain settlement situations.",
      },
      {
        term: "Request-only table",
        definition:
          "A table where mobile RSVP or waitlist requests require staff approval.",
      },
      {
        term: "Referred by",
        definition:
          "The existing player who brought in a new player. This can be changed before the new player's first deposit and is locked after that deposit.",
      },
      {
        term: "Referee bonus",
        definition:
          "The bonus given to the new referred player when first-deposit referral bonus rules apply.",
      },
      {
        term: "Referral bonus",
        definition:
          "The bonus given to the player who referred a new player when the referred player makes their first deposit.",
      },
      {
        term: "Roll balance",
        definition:
          "A workflow that carries remaining player balance or debt forward instead of fully settling immediately.",
      },
      {
        term: "RSVP",
        definition:
          "A pre-table or pre-seat interest/reservation list used before seats are finalized.",
      },
      {
        term: "Session",
        definition:
          "An operational period that groups tables, transactions, tips, rake, cash, wallets, and reports.",
      },
      {
        term: "Service staff",
        definition:
          "Non-dealer workers who may receive chip tips, such as servers, waitresses, cooks, or massage staff.",
      },
      {
        term: "Settle",
        definition:
          "A workflow that subtracts what a player owes from a withdrawal or cashout.",
      },
      {
        term: "Tournament cash bag",
        definition:
          "Tournament-specific physical cash tracking, separate from the cash-game cash bag.",
      },
      {
        term: "Tournament credit",
        definition:
          "Credit used in tournament operations. It is separate from cash session credit but can affect the shared player book.",
      },
      {
        term: "Wallet",
        definition:
          "An electronic payment method or tracked account used for deposits, withdrawals, costs, tips, payouts, and settlements.",
      },
      {
        term: "Waitlist",
        definition:
          "The ordered list of players waiting for a seat at a table.",
      },
    ],
  },
];

const DEFAULT_OPEN_SECTIONS = [
  "getting-started",
  "core-concepts",
  "money-credit-books-wallets",
];

const normalizeText = (value) => String(value || "").toLowerCase();

const collectExampleText = (example) => {
  if (!example) return "";
  return [example.title, ...(example.body || []), ...(example.bullets || [])].join(" ");
};

const collectTopicText = (topic) =>
  [
    topic.title,
    ...(topic.body || []),
    ...(topic.bullets || []),
    ...(topic.steps || []),
    topic.warning,
    collectExampleText(topic.example),
  ].join(" ");

const filterSections = (sections, searchTerm) => {
  const query = normalizeText(searchTerm.trim());
  if (!query) return sections;

  return sections
    .map((section) => {
      const sectionMatches = normalizeText(
        [section.title, section.eyebrow, section.summary].join(" "),
      ).includes(query);

      if (sectionMatches) return section;

      const topics = (section.topics || []).filter((topic) =>
        normalizeText(collectTopicText(topic)).includes(query),
      );
      const terms = (section.terms || []).filter(({ term, definition }) =>
        normalizeText(`${term} ${definition}`).includes(query),
      );

      if (!topics.length && !terms.length) return null;

      return {
        ...section,
        topics,
        terms,
      };
    })
    .filter(Boolean);
};

function TopicBlock({ topic }) {
  return (
    <section className="software-guide-topic">
      <h3>{topic.title}</h3>
      {topic.body?.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
      {topic.steps && (
        <ol>
          {topic.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      )}
      {topic.bullets && (
        <ul>
          {topic.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      )}
      {topic.example && (
        <div className="software-guide-callout software-guide-callout--example">
          <strong>{topic.example.title}</strong>
          {topic.example.body?.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          {topic.example.bullets && (
            <ul>
              {topic.example.bullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {topic.warning && (
        <div className="software-guide-callout software-guide-callout--warning">
          <AlertTriangle size={18} aria-hidden="true" />
          <p>{topic.warning}</p>
        </div>
      )}
    </section>
  );
}

function Glossary({ terms }) {
  return (
    <dl className="software-guide-glossary">
      {terms.map(({ term, definition }) => (
        <div key={term}>
          <dt>{term}</dt>
          <dd>{definition}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function SoftwareGuide() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission") || "";
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  const [searchTerm, setSearchTerm] = useState("");
  const [openSectionIds, setOpenSectionIds] = useState(
    () => new Set(DEFAULT_OPEN_SECTIONS),
  );

  useEffect(() => {
    if (!token) {
      navigate("/");
    }
  }, [navigate, token]);

  const filteredSections = useMemo(
    () => filterSections(GUIDE_SECTIONS, searchTerm),
    [searchTerm],
  );

  const resultCount = useMemo(
    () =>
      filteredSections.reduce(
        (count, section) =>
          count + (section.topics?.length || 0) + (section.terms?.length || 0),
        0,
      ),
    [filteredSections],
  );

  const goToWorkspace = () => {
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealerdashboard`);
      return;
    }
    if (permission.includes("Y")) {
      navigate(`/${cardroom}/tournamentsHome`);
      return;
    }
    if (permission.includes("V")) {
      navigate(`/${cardroom}/admin`);
      return;
    }
    navigate(`/${cardroom}/game`);
  };

  const logOut = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  const toggleSection = (sectionId) => {
    setOpenSectionIds((current) => {
      const next = new Set(current);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const openAll = () => {
    setOpenSectionIds(new Set(GUIDE_SECTIONS.map((section) => section.id)));
  };

  const collapseAll = () => {
    setOpenSectionIds(new Set());
  };

  const jumpToSection = (sectionId) => {
    setOpenSectionIds((current) => {
      const next = new Set(current);
      next.add(sectionId);
      return next;
    });
    window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 40);
  };

  if (!token) return null;

  return (
    <main className="nexus-home software-guide-page">
      <header className="nexus-nav software-guide-nav">
        <button
          type="button"
          className="software-guide-brand"
          onClick={goToWorkspace}
        >
          <span className="nexus-nav__mark">N</span>
          <span>Software Guide</span>
        </button>
        <div className="software-guide-nav__actions">
          <button
            type="button"
            className="nexus-button nexus-button--secondary"
            onClick={goToWorkspace}
          >
            <ArrowLeft size={17} aria-hidden="true" />
            Back to Workspace
          </button>
          <button
            type="button"
            className="nexus-button nexus-button--ghost"
            onClick={logOut}
          >
            <LogOut size={17} aria-hidden="true" />
            Log Out
          </button>
        </div>
      </header>

      <section className="software-guide-hero">
        <div className="software-guide-hero__copy">
          <p className="nexus-eyebrow">Private workspace manual</p>
          <h1>Software Guide</h1>
          <p>
            Use this guide to understand what each area of the software does,
            when to use it, and how daily operations, money tracking, staff,
            reports, tournaments, and Nexus Mobile connect.
          </p>
          <div className="software-guide-hero__actions">
            <button
              type="button"
              className="nexus-button nexus-button--primary"
              onClick={() => jumpToSection("getting-started")}
            >
              <ListChecks size={18} aria-hidden="true" />
              Start With Setup
            </button>
            <button
              type="button"
              className="nexus-button nexus-button--secondary"
              onClick={() => jumpToSection("money-credit-books-wallets")}
            >
              <CircleDollarSign size={18} aria-hidden="true" />
              Understand Money Flow
            </button>
          </div>
        </div>
        <div className="software-guide-hero__panel" aria-label="Guide highlights">
          <div>
            <BookOpen size={21} aria-hidden="true" />
            <strong>How to use this guide</strong>
            <span>Start with setup, jump by section, or search for the exact topic you need.</span>
          </div>
          <div>
            <WalletCards size={21} aria-hidden="true" />
            <strong>Understand the money flow</strong>
            <span>Learn how books, credit, cash bags, wallets, balances, and debt work together.</span>
          </div>
          <div>
            <Trophy size={21} aria-hidden="true" />
            <strong>Run day-to-day operations</strong>
            <span>Use it as a reference for players, staff, tables, reports, mobile requests, and tournaments.</span>
          </div>
        </div>
      </section>

      <div className="software-guide-shell">
        <aside className="software-guide-sidebar" aria-label="Guide navigation">
          <label className="software-guide-search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search credit, wallets, waitlist..."
            />
          </label>
          <div className="software-guide-search__meta">
            {searchTerm.trim()
              ? `${resultCount} matching guide items`
              : `${GUIDE_SECTIONS.length} guide sections`}
          </div>
          <div className="software-guide-sidebar__actions">
            <button type="button" onClick={openAll}>
              Expand all
            </button>
            <button type="button" onClick={collapseAll}>
              Collapse all
            </button>
          </div>
          <nav>
            {GUIDE_SECTIONS.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  type="button"
                  key={section.id}
                  onClick={() => jumpToSection(section.id)}
                >
                  <Icon size={17} aria-hidden="true" />
                  <span>{section.title}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="software-guide-content" aria-label="Guide content">
          {filteredSections.length === 0 && (
            <div className="software-guide-empty">
              <HelpCircle size={26} aria-hidden="true" />
              <h2>No guide entries found</h2>
              <p>
                Try searching for a broader term like credit, book, wallet,
                session, tournament, player, report, or mobile.
              </p>
              <button
                type="button"
                className="nexus-button nexus-button--primary"
                onClick={() => setSearchTerm("")}
              >
                Clear Search
              </button>
            </div>
          )}

          {filteredSections.map((section) => {
            const Icon = section.icon;
            const isOpen =
              Boolean(searchTerm.trim()) || openSectionIds.has(section.id);

            return (
              <article
                className="software-guide-section"
                id={section.id}
                key={section.id}
              >
                <button
                  type="button"
                  className="software-guide-section__header"
                  aria-expanded={Boolean(isOpen)}
                  onClick={() => toggleSection(section.id)}
                >
                  <span className="software-guide-section__icon">
                    <Icon size={22} aria-hidden="true" />
                  </span>
                  <span className="software-guide-section__titles">
                    <span className="nexus-eyebrow">{section.eyebrow}</span>
                    <strong>{section.title}</strong>
                    <em>{section.summary}</em>
                  </span>
                  <ChevronDown
                    className={isOpen ? "is-open" : ""}
                    size={22}
                    aria-hidden="true"
                  />
                </button>

                {isOpen && (
                  <div className="software-guide-section__body">
                    {section.topics?.map((topic) => (
                      <TopicBlock key={topic.title} topic={topic} />
                    ))}
                    {section.terms && <Glossary terms={section.terms} />}
                  </div>
                )}
              </article>
            );
          })}
        </section>
      </div>
    </main>
  );
}
